import React, { useState, useEffect } from 'react';
import { getProductosComercio, eliminarProducto } from '../api/database';
import ProductoModal from '../components/ProductoModal';
import EditarProductoModal from '../components/EditarProductoModal';
import { useLogger } from '../utils/loggers';
import usePopup from '../hooks/usePopup';
import SimpleToast from '../components/SimpleToast';
import PreloadSpinner from '../components/PreloadSpinner.jsx';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Package,
  Tag,
  Info,
  Calendar,
  X
} from 'lucide-react';

// Funciones auxiliares para obtener nombres legibles
const getNombreClasificacion = (id) => {
  const clasificaciones = {
    1: 'Omnívoro',
    2: 'Vegetariano',
    3: 'Vegano',
    4: 'Sin Gluten',
    5: 'Sin Lactosa',
    6: 'Sin TACC',
    7: 'Ayurvédica',
    8: 'Frutas y Verduras',
    9: 'Lácteos',
    10: 'Carnes',
    11: 'Cereales y Granos',
    12: 'Bebidas',
    13: 'Orgánico',
    14: 'Natural',
    15: 'Producto General'
  };
  return clasificaciones[id] || `ID: ${id}`;
};

const getNombreTipoPackaging = (id) => {
  const tipos = {
    1: 'Unidad',
    2: 'Kilo',
    3: 'Gramos',
    4: 'Litro',
    5: 'Caja',
    6: 'Docena',
    7: 'Metro',
    8: 'Bolsa'
  };
  return tipos[id] || 'Unidad';
};

const Productos = () => {
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showValorizacionModal, setShowValorizacionModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { log } = useLogger();
  const { toast, showSuccess, showError, showWarning, showInfo, hideToast, showConfirm, handleConfirm, handleCancel } = usePopup();



  // Efecto para monitorear cambios en el estado del popup
  useEffect(() => {
    if (showDetailModal) {
      // Cerrar otros modales cuando se abra el popup de detalles
    }
  }, [showDetailModal, selectedProduct]);

  // Cargar productos al montar el componente
  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    try {
      log('🔍 [DEBUG] loadProductos ejecutándose...');
      setLoading(true);
      log('🔍 [DEBUG] Llamando getProductosComercio...');
      const response = await getProductosComercio();
      
      if (response.success) {
        log('🔍 [DEBUG] Productos cargados exitosamente:', response.productos.length);
        setProductos(response.productos);
        setFilteredProductos(response.productos);
        
        // Log detallado de cada producto
        response.productos.forEach(producto => {
          log(`🔍 [DEBUG] Producto cargado: ID=${producto.id}, Código=${producto.codigo_personalizado}, Nombre=${producto.nombre}, Stock=${producto.stock}`);
        });
      } else {
        log('🔍 [ERROR] Error al cargar productos:', response.error);
        setProductos([]);
        setFilteredProductos([]);
      }
    } catch (error) {
      log('🔍 [ERROR] Excepción al cargar productos:', error.message);
      setProductos([]);
      setFilteredProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.trim() === '') {
      setFilteredProductos(productos);
    } else {
      const filtered = productos.filter(producto =>
        producto.nombre.toLowerCase().includes(term.toLowerCase()) ||
        producto.codigo_personalizado.toLowerCase().includes(term.toLowerCase()) ||
        producto.marca.toLowerCase().includes(term.toLowerCase()) ||
        (producto.clasificacion_especial_nombre && producto.clasificacion_especial_nombre.toLowerCase().includes(term.toLowerCase()))
      );
      setFilteredProductos(filtered);
      
      // NO mostrar popup automáticamente al escribir
      // Solo se mostrará cuando se presione Enter o el botón Buscar
    }
  };

  const mostrarPopupBusquedaSinResultados = (termino) => {
    // Crear popup personalizado para búsqueda sin resultados
    const popupElement = document.createElement('div');
    popupElement.innerHTML = `
      <div style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background-color: rgba(0, 0, 0, 0.7) !important;
        z-index: 999999 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        visibility: visible !important;
        opacity: 1 !important;
      ">
        <div style="
          background: white !important;
          border-radius: 12px !important;
          padding: 2rem !important;
          max-width: 400px !important;
          width: 90% !important;
          text-align: center !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #e5e7eb !important;
        ">
          <div style="
            width: 64px !important;
            height: 64px !important;
            background: #fef3c7 !important;
            border-radius: 50% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 auto 1rem auto !important;
          ">
            <svg style="width: 32px !important; height: 32px !important; color: #f59e0b !important;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          
          <h3 style="
            font-size: 1.25rem !important;
            font-weight: 600 !important;
            color: #1f2937 !important;
            margin: 0 0 0.5rem 0 !important;
            line-height: 1.4 !important;
          ">
            Búsqueda sin resultados
          </h3>
          
          <p style="
            color: #6b7280 !important;
            margin: 0 0 1.5rem 0 !important;
            line-height: 1.5 !important;
            font-size: 0.95rem !important;
          ">
            No se encontraron productos que coincidan con<br>
            <strong style="color: #374151 !important;">"${termino}"</strong>
          </p>
          
          <div style="
            display: flex !important;
            gap: 0.75rem !important;
            justify-content: center !important;
          ">
            <button id="cerrar-popup-busqueda" style="
              background: #3b82f6 !important;
              color: white !important;
              border: none !important;
              padding: 0.75rem 1.5rem !important;
              border-radius: 8px !important;
              font-weight: 500 !important;
              font-size: 0.9rem !important;
              cursor: pointer !important;
              transition: background-color 0.2s !important;
              min-width: 100px !important;
            " onmouseover="this.style.backgroundColor='#2563eb'" onmouseout="this.style.backgroundColor='#3b82f6'">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    `;

    // Agregar al DOM
    document.body.appendChild(popupElement);

    // Forzar estilos después de agregar al DOM
    setTimeout(() => {
      popupElement.style.display = 'flex';
      popupElement.style.visibility = 'visible';
      popupElement.style.opacity = '1';
    }, 10);

    // Event listener para cerrar
    const cerrarBtn = document.getElementById('cerrar-popup-busqueda');
    if (cerrarBtn) {
      cerrarBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (popupElement && popupElement.parentNode) {
          document.body.removeChild(popupElement);
        }
      });
    }

    // Cerrar al hacer click en el overlay
    popupElement.addEventListener('click', (e) => {
      if (e.target === popupElement) {
        if (popupElement && popupElement.parentNode) {
          document.body.removeChild(popupElement);
        }
      }
    });

    // Cerrar con Escape
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (popupElement && popupElement.parentNode) {
          document.body.removeChild(popupElement);
        }
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  };

  const handleProductAdded = (action, producto) => {
    console.log('🔄 [PRODUCTOS] handleProductAdded llamado:', { action, producto });
    if (action === 'add') {
      console.log('➕ [PRODUCTOS] Agregando producto nuevo');
      setProductos(prev => [producto, ...prev]);
      setFilteredProductos(prev => [producto, ...prev]);
    } else if (action === 'edit') {
      console.log('✏️ [PRODUCTOS] Actualizando producto existente - ACTUALIZANDO LISTA LOCAL');
      // Actualizar la lista localmente sin recargar la página
      setProductos(prev => prev.map(p => p.id === producto.id ? producto : p));
      setFilteredProductos(prev => prev.map(p => p.id === producto.id ? producto : p));
      return;
    }
    console.log('✅ [PRODUCTOS] Estado actualizado');
  };

  const handleEdit = (producto) => {
    console.log('🔧 [EDIT] Iniciando edición de producto:', producto);
    // Usar ProductoModal en lugar de EditarProductoModal para mantener consistencia
    setSelectedProduct(producto);
    setShowModal(true);
    console.log('🔧 [EDIT] Modal abierto, selectedProduct:', producto);
  };

  const handleValorizacion = (producto) => {
    setSelectedProduct(producto);
    setShowValorizacionModal(true);
  };

  const handleDelete = async (id) => {
    showConfirm('¿Estás seguro de que quieres eliminar este producto?', 'Confirmar Eliminación', {
      onConfirm: async () => {
        try {
          console.log('🗑️ Eliminando producto ID:', id);
          const response = await eliminarProducto(id);
          
          if (response.success) {
            console.log('✅ Producto eliminado exitosamente');
            showSuccess('Producto eliminado exitosamente', 'Eliminación Exitosa');
            setProductos(prev => prev.filter(p => p.id !== id));
            setFilteredProductos(prev => prev.filter(p => p.id !== id));
          } else {
            console.error('❌ Error del servidor:', response.error);
            showError(`Error al eliminar: ${response.error || 'Error desconocido'}`, 'Error del Servidor');
          }
        } catch (error) {
          console.error('❌ Error de conexión:', error);
          showError(`Error de conexión: ${error.message}`, 'Error de Conexión');
        }
      }
    });
  };

  const handleShowDetail = (producto) => {
    setSelectedProduct(producto);
    setShowDetailModal(true);
  };

  const formatPrice = (price) => {
    if (!price) return '$0,00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatStock = (stock) => {
    if (!stock && stock !== 0) return '0';
    return stock.toString();
  };

  const getStockStatus = (stock, stockMin) => {
    if (stock <= 0) {
      return { text: 'Sin Stock', color: 'stock-status out', icon: '🔴' };
    } else if (stock <= (stockMin || 10)) {
      return { text: 'Bajo Stock', color: 'stock-status low', icon: '🟡' };
    } else {
      return { text: 'Disponible', color: 'stock-status available', icon: '🟢' };
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedProductos = () => {
    if (!sortConfig.key) return filteredProductos;

    return [...filteredProductos].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Manejar casos especiales
      if (sortConfig.key === 'estado') {
        aValue = getStockStatus(a.stock_actual, a.stock_actual_minimo).text;
        bValue = getStockStatus(b.stock_actual, b.stock_actual_minimo).text;
      }

      // Convertir a string para comparación
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getFilteredAndSortedProductos = () => {
    let productosToShow = filteredProductos;
    
    // Aplicar filtro de bajo stock si está activo
    if (showLowStockOnly) {
      productosToShow = filteredProductos.filter(producto => 
        producto.stock_actual <= producto.stock_minimo
      );
    }
    
    // Aplicar ordenamiento si está configurado
    if (!sortConfig.key) return productosToShow;

    return [...productosToShow].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Manejar casos especiales
      if (sortConfig.key === 'estado') {
        aValue = getStockStatus(a.stock_actual, a.stock_actual_minimo).text;
        bValue = getStockStatus(b.stock_actual, b.stock_actual_minimo).text;
      }

      // Convertir a string para comparación
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="sort-icon inactive">↕</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="sort-icon active">↑</span> : 
      <span className="sort-icon active">↓</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <PreloadSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="page-header rounded-2xl shadow-lg p-6 mb-8 animate-slide-in">
          {/* Título y controles en una sola línea */}
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Título y descripción */}
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2">
                🛍️ Gestión de Productos
              </h1>
              <p className="text-gray-600 text-lg">
                Administra tu inventario de manera eficiente
              </p>
            </div>
            
            {/* Controles principales: Botón y Buscador */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Botón Nuevo Producto - Más chico */}
              <button
                onClick={() => setShowModal(true)}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Producto</span>
              </button>

              {/* Buscador con botón de búsqueda */}
              <div className="search-with-button flex items-center gap-2">
                <div className="search-input-wrapper relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="🔍 Buscar por nombre, código, marca o categoría dietética..."
                    value={searchTerm}
                    onChange={handleSearch}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        if (searchTerm.trim()) {
                          const filtered = productos.filter(producto =>
                            producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            producto.codigo_personalizado.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            producto.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (producto.clasificacion_especial_nombre && producto.clasificacion_especial_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
                          );
                          setFilteredProductos(filtered);
                          
                          // Mostrar popup si no se encontraron resultados
                          if (filtered.length === 0) {
                            mostrarPopupBusquedaSinResultados(searchTerm);
                          }
                        }
                      }
                    }}
                    className="search-input-compact pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm w-48"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilteredProductos(productos);
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors duration-200 p-1 rounded-full hover:bg-red-50"
                      title="Limpiar búsqueda"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Botón de Búsqueda */}
                <button
                  onClick={() => {
                    if (searchTerm.trim()) {
                      // Forzar actualización de la búsqueda
                      const filtered = productos.filter(producto =>
                        producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        producto.codigo_personalizado.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        producto.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (producto.clasificacion_especial_nombre && producto.clasificacion_especial_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
                      );
                      setFilteredProductos(filtered);
                      
                      // Mostrar popup si no se encontraron resultados
                      if (filtered.length === 0) {
                        mostrarPopupBusquedaSinResultados(searchTerm);
                      }
                    }
                  }}
                  className="btn-success"
                  disabled={!searchTerm.trim()}
                  title={searchTerm.trim() ? "Ejecutar búsqueda" : "Escribe algo para buscar"}
                >
                  <Search className="w-4 h-4" />
                  <span>Buscar</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards - Layout Horizontal en Línea */}
        <div className="stats-container rounded-2xl shadow-lg p-6 mb-8">
          <div className="stats-horizontal">
            {/* Total Productos */}
            <div className="stats-compact stats-blue flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center">
              <div className="icon-container p-2 bg-blue-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-blue-700">Total Productos</p>
                <p className="text-2xl font-bold text-blue-900">{productos.length}</p>
              </div>
            </div>

            {/* Stock Total */}
            <div className="stats-compact stats-green flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center">
              <div className="icon-container p-2 bg-green-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-green-700">Stock Total</p>
                <p className="text-2xl font-bold text-green-900">
                  {productos.reduce((sum, p) => sum + (p.stock || 0), 0)}
                </p>
              </div>
            </div>

            {/* Bajo Stock */}
            <div 
              className={`stats-compact stats-yellow flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center cursor-pointer transition-all duration-200 ${
                showLowStockOnly ? 'ring-2 ring-yellow-400 shadow-lg' : 'hover:shadow-md'
              }`}
              onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              title={showLowStockOnly ? "Click para ver todos los productos" : "Click para ver solo productos con bajo stock"}
            >
              <div className="icon-container p-2 bg-yellow-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-yellow-700">
                  {showLowStockOnly ? "Filtro Activo" : "Bajo Stock"}
                </p>
                <p className="text-2xl font-bold text-yellow-900">
                  {productos.filter(p => p.stock_actual <= p.stock_minimo).length}
                </p>
                {showLowStockOnly && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Mostrando solo bajo stock
                  </p>
                )}
              </div>
            </div>

            {/* Valor Total */}
            <div className="stats-compact stats-purple flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center">
              <div className="icon-container p-2 bg-purple-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-purple-700">Valor Total</p>
                <p className="text-2xl font-bold text-purple-900">
                  {formatPrice(productos.reduce((sum, p) => sum + ((p.precio_venta || 0) * (p.stock_actual || 0)), 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Table - Tabla Profesional */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header de la tabla */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Lista de Productos</h3>
                <p className="text-sm text-gray-600">
                  {showLowStockOnly 
                    ? `Mostrando solo productos con bajo stock (${getFilteredAndSortedProductos().length} productos)`
                    : 'Gestiona tu inventario de productos'
                  }
                </p>
              </div>
              {showLowStockOnly && (
                <button
                  onClick={() => setShowLowStockOnly(false)}
                  className="btn-warning"
                >
                  <X className="w-4 h-4" />
                  <span>Ver Todos</span>
                </button>
              )}
            </div>
          </div>

          {/* Vista de escritorio - Tabla */}
          <div className="desktop-table-view overflow-x-auto">
            <table className="w-full products-table">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/4">
                    Producto
                  </th>
                  <th 
                    className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/6 cursor-pointer hover:bg-blue-700 transition-colors duration-200"
                    onClick={() => handleSort('codigo_personalizado')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Código</span>
                      <span className="text-lg">{getSortIcon('codigo_personalizado')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/8 cursor-pointer hover:bg-blue-700 transition-colors duration-200"
                    onClick={() => handleSort('categoria')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Categoría</span>
                      <span className="text-lg">{getSortIcon('categoria')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/8 cursor-pointer hover:bg-blue-700 transition-colors duration-200"
                    onClick={() => handleSort('clasificacion_especial_nombre')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Dietética</span>
                      <span className="text-lg">{getSortIcon('clasificacion_especial_nombre')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/8">
                    Precio
                  </th>
                  <th className="px-3 py-4 text-center text-sm font-semibold uppercase tracking-wider w-1/8">
                    Stock
                  </th>
                  <th 
                    className="px-3 py-4 text-center text-sm font-semibold uppercase tracking-wider w-1/8 cursor-pointer hover:bg-blue-700 transition-colors duration-200"
                    onClick={() => handleSort('estado')}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Estado</span>
                      <span className="text-lg">{getSortIcon('estado')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold uppercase tracking-wider w-1/8">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredAndSortedProductos().map((producto) => {
                  const status = getStockStatus(producto.stock_actual, producto.stock_minimo);
                  
                  return (
                    <tr key={producto.id} className="hover:bg-gray-50 transition-colors duration-200">
                      {/* Columna Producto */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="product-image-container">
                            {producto.imagen_url && 
                             producto.imagen_url.trim() !== '' && 
                             !producto.imagen_url.startsWith('blob:') ? (
                              <img
                                src={producto.imagen_url}
                                alt={producto.nombre}
                                className="product-image"
                                onError={(e) => {
                                  if (e.target && e.target.style) {
                                    e.target.style.display = 'none';
                                  }
                                  if (e.target && e.target.nextSibling && e.target.nextSibling.style) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : (
                              <div className="product-image-placeholder">
                                <Package className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {producto.nombre}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {producto.marca || 'Sin marca'}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Columna Código */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">
                          {producto.codigo_personalizado || 'N/A'}
                        </div>
                      </td>

                      {/* Columna Categoría */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {producto.categoria || 'General'}
                        </div>
                      </td>

                      {/* Columna Categoría Dietética */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          {producto.clasificacion_especial_nombre ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              {producto.clasificacion_especial_nombre}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Sin clasificar</span>
                          )}
                        </div>
                      </td>

                      {/* Columna Precio */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-right">
                          <div className="text-sm font-bold text-green-600">
                            {formatPrice(producto.precio_venta)}
                          </div>
                          {producto.precio_compra > 0 && (
                            <div className="text-xs text-gray-500">
                              Compra: {formatPrice(producto.precio_compra)}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Columna Stock */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-center">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatStock(producto.stock_actual)}
                          </div>
                          {producto.stock_minimo > 0 && (
                            <div className="text-xs text-gray-500">
                              Mín: {formatStock(producto.stock_minimo)}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Columna Estado */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <span className="mr-1">{status.icon}</span>
                          {status.text}
                        </span>
                      </td>

                      {/* Columna Acciones */}
                      <td className="px-3 py-2 text-center">
                        <div className="actions-container">
                          <button
                            onClick={() => handleShowDetail(producto)}
                            className="btn-action btn-primary"
                            title="Ver producto"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(producto)}
                            className="btn-action btn-success"
                            title="Editar producto"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleValorizacion(producto)}
                            className="btn-action btn-warning"
                            title="Valorización del inventario"
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                          {producto.stock === 0 && (
                            <button
                              onClick={() => handleDelete(producto.id)}
                              className="btn-action btn-danger"
                              title="Eliminar producto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Vista móvil - Tarjetas */}
          <div className="mobile-cards-view">
            <div className="space-y-3 p-4">
              {getFilteredAndSortedProductos().map((producto) => {
                const status = getStockStatus(producto.stock_actual, producto.stock_minimo);
                
                return (
                  <div key={producto.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                    {/* Header de la tarjeta */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Imagen del producto */}
                        <div className="product-image-container-mobile">
                          {producto.imagen_url && 
                           producto.imagen_url.trim() !== '' && 
                           !producto.imagen_url.startsWith('blob:') ? (
                            <img
                              src={producto.imagen_url}
                              alt={producto.nombre}
                              className="product-image-mobile"
                              onError={(e) => {
                                if (e.target && e.target.style) {
                                  e.target.style.display = 'none';
                                }
                                if (e.target && e.target.nextSibling && e.target.nextSibling.style) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : (
                            <div className="product-image-placeholder-mobile">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Información del producto */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {producto.nombre}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {producto.marca || 'Sin marca'}
                          </p>
                          <p className="text-xs text-gray-400 font-mono mt-1">
                            Código: {producto.codigo_personalizado || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Estado del stock */}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color} flex-shrink-0`}>
                        <span className="mr-1">{status.icon}</span>
                        {status.text}
                      </span>
                    </div>

                    {/* Información detallada */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      {/* Precio */}
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Precio</p>
                        <p className="text-sm font-bold text-green-600">
                          {formatPrice(producto.precio_venta)}
                        </p>
                        {producto.precio_compra > 0 && (
                          <p className="text-xs text-gray-400">
                            Compra: {formatPrice(producto.precio_compra)}
                          </p>
                        )}
                      </div>
                      
                      {/* Stock */}
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Stock</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatStock(producto.stock_actual)}
                        </p>
                        {producto.stock_minimo > 0 && (
                          <p className="text-xs text-gray-400">
                            Mín: {formatStock(producto.stock_minimo)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Categorías */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {producto.categoria && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {producto.categoria}
                        </span>
                      )}
                      {producto.clasificacion_especial_nombre && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {producto.clasificacion_especial_nombre}
                        </span>
                      )}
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleShowDetail(producto)}
                        className="btn-action-mobile btn-primary"
                        title="Ver producto"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(producto)}
                        className="btn-action-mobile btn-success"
                        title="Editar producto"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleValorizacion(producto)}
                        className="btn-action-mobile btn-warning"
                        title="Valorización del inventario"
                      >
                        <Tag className="w-4 h-4" />
                      </button>
                      {producto.stock === 0 && (
                        <button
                          onClick={() => handleDelete(producto.id)}
                          className="btn-action-mobile btn-danger"
                          title="Eliminar producto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mensaje cuando no hay productos */}
          {filteredProductos.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No se encontraron productos</p>
              <p className="text-gray-400">Intenta ajustar tu búsqueda o crear un nuevo producto</p>
            </div>
          )}
        </div>

        {/* Modal para nuevo producto */}
        {showModal && (
          <ProductoModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              setSelectedProduct(null);
              // Refrescar la lista de productos al cerrar el modal
              loadProductos();
            }}
            onProductAdded={handleProductAdded}
            productoExistente={selectedProduct}
          />
        )}





        {/* Popup de detalles del producto - Compacto y Eficiente */}
        {showDetailModal && selectedProduct && (
          <div className="popup-overlay">
            <div className="product-detail-popup">
              {/* Header del popup */}
              <div className="popup-header">
                <h3>📋 {selectedProduct.nombre}</h3>
                <p>{selectedProduct.marca || 'Sin marca'}</p>
                
                {/* Botón de cerrar */}
                <button
                  className="popup-close-btn"
                  onClick={() => setShowDetailModal(false)}
                  title="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Contenido del popup */}
              <div className="popup-content">
                <div className="popup-grid">
                  {/* Columna izquierda - Imagen y estado */}
                  <div className="flex flex-col items-center">
                    {/* Imagen del producto */}
                    {selectedProduct.imagen_url ? (
                      <img 
                        src={selectedProduct.imagen_url} 
                        alt={selectedProduct.nombre}
                        className="popup-product-image"
                      />
                    ) : (
                      <div className="popup-product-image bg-gray-200 flex items-center justify-center">
                        <Package className="w-20 h-20 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Estado del stock */}
                    <div className="text-center">
                      {(() => {
                        const stock = selectedProduct.stock_actual || 0;
                        const stockMin = selectedProduct.stock_minimo || 0;
                        
                        if (stock <= 0) {
                          return (
                            <span className="popup-status-badge bg-red-500 text-white">
                              🔴 Sin Stock
                            </span>
                          );
                        } else if (stock <= stockMin) {
                          return (
                            <span className="popup-status-badge bg-yellow-500 text-white">
                              🟡 Bajo Stock
                            </span>
                          );
                        } else {
                          return (
                            <span className="popup-status-badge bg-green-500 text-white">
                              🟢 Disponible
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </div>
                  
                  {/* Columna derecha - Información organizada */}
                  <div className="space-y-2">
                    {/* Información básica */}
                    <div className="popup-section">
                      <h4>📋 Básico</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card">
                          <div className="label">Código</div>
                          <div className="value">
                            {selectedProduct.codigo_personalizado || 'N/A'}
                          </div>
                        </div>
                        <div className="info-card">
                          <div className="label">Categoría</div>
                          <div className="value">
                            {selectedProduct.categoria || 'General'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Descripción - MOVIDA DESPUÉS del código */}
                    {selectedProduct.descripcion && (
                      <div className="popup-section">
                        <h4>📝 Descripción</h4>
                        <div className="info-card">
                          <p className="text-gray-700 text-xs leading-tight">
                            {selectedProduct.descripcion}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Precios */}
                    <div className="popup-section">
                      <h4>💰 Precios</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card bg-green-50 border-green-200">
                          <div className="label">Venta</div>
                          <div className="value large text-green-800">
                            {formatPrice(selectedProduct.precio_venta)}
                          </div>
                        </div>
                        
                        {selectedProduct.precio_compra > 0 && (
                          <div className="info-card bg-blue-50 border-blue-200">
                            <div className="label">Compra</div>
                            <div className="value medium text-blue-800">
                              {formatPrice(selectedProduct.precio_compra)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stock */}
                    <div className="popup-section">
                      <h4>📦 Stock</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card bg-purple-50 border-purple-200">
                          <div className="label">Actual</div>
                          <div className="value medium text-purple-800">
                            {formatStock(selectedProduct.stock_actual)}
                          </div>
                        </div>
                        
                        <div className="info-card bg-yellow-50 border-yellow-200">
                          <div className="label">Mínimo</div>
                          <div className="value medium text-yellow-800">
                            {formatStock(selectedProduct.stock_minimo)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Clasificación Dietética */}
                    {selectedProduct.clasificacion_principal_id && (
                      <div className="popup-section">
                        <h4>🥗 Clasificación Dietética</h4>
                        <div className="grid grid-cols-2 gap-1.5">
                          <div className="info-card bg-orange-50 border-orange-200">
                            <div className="label">Clasificación</div>
                            <div className="value medium text-orange-800">
                              {getNombreClasificacion(selectedProduct.clasificacion_principal_id)}
                            </div>
                          </div>
                          <div className="info-card bg-blue-50 border-blue-200">
                            <div className="label">Fuente</div>
                            <div className="value medium text-blue-800">
                              {selectedProduct.fuente_clasificacion || 'Manual'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tipo de Envase y Packaging */}
                    <div className="popup-section">
                      <h4>📦 Presentación del Producto</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card bg-green-50 border-green-200">
                          <div className="label">Tipo</div>
                          <div className="value medium text-green-800">
                            {getNombreTipoPackaging(selectedProduct.tipo_packaging_id) || 'Unidad'}
                          </div>
                        </div>
                        <div className="info-card bg-purple-50 border-purple-200">
                          <div className="label">Cantidad</div>
                          <div className="value medium text-purple-800">
                            {selectedProduct.cantidad_packaging ? 
                              `${selectedProduct.cantidad_packaging} ${getNombreTipoPackaging(selectedProduct.tipo_packaging_id) || 'unidades'}` : 
                              '1 Unidad'
                            }
                          </div>
                        </div>
                      </div>
                      
                      {/* Descripción completa del packaging */}
                      {selectedProduct.packaging_completo && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="text-sm font-medium text-blue-800">
                            📦 {selectedProduct.packaging_completo}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Botones de acción - FUERA del popup-content para que estén al final */}
              <div className="popup-actions">
                <div className="actions-container">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="btn-secondary"
                    title="Cerrar popup"
                  >
                    <X className="w-4 h-4" />
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Popup de Valorización del Inventario */}
        {showValorizacionModal && selectedProduct && (
          <div className="popup-overlay">
            <div className="product-detail-popup">
              {/* Header del popup */}
              <div className="popup-header">
                <h3>📈 Valorización del Inventario</h3>
                <p>{selectedProduct.nombre}</p>
                
                {/* Botón de cerrar */}
                <button
                  className="popup-close-btn"
                  onClick={() => setShowValorizacionModal(false)}
                  title="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Contenido del popup */}
              <div className="popup-content">
                <div className="grid">
                  {/* Columna izquierda - Información del producto */}
                  <div className="flex flex-col items-center justify-center">
                    {/* Imagen del producto */}
                    {selectedProduct.imagen_url ? (
                      <img 
                        src={selectedProduct.imagen_url} 
                        alt={selectedProduct.nombre}
                        className="popup-product-image mx-auto"
                      />
                    ) : (
                      <div className="popup-product-image bg-yellow-100 flex items-center justify-center mx-auto">
                        <span className="text-4xl">💰</span>
                      </div>
                    )}
                    
                    {/* Código del producto */}
                    <div className="text-center mt-2">
                      <span className="popup-status-badge bg-blue-500 text-white">
                        Código: {selectedProduct.codigo_personalizado}
                      </span>
                    </div>
                  </div>
                  
                  {/* Columna derecha - Valorización */}
                  <div className="space-y-2">
                    {/* Stock Actual */}
                    <div className="popup-section">
                      <h4>📦 Stock Actual</h4>
                      <div className="grid grid-cols-1 gap-1.5">
                        <div className="info-card bg-blue-50 border-blue-200">
                          <div className="label">Unidades</div>
                          <div className="value large text-blue-800">
                            {parseInt(selectedProduct.stock_actual) || 0} unidades
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Valorización por Compra */}
                    <div className="popup-section">
                      <h4>💚 Valor por Compra</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card bg-green-50 border-green-200">
                          <div className="label">Unitario</div>
                          <div className="value medium text-green-800">
                            ${(parseFloat(selectedProduct.precio_compra) || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="info-card bg-green-100 border-green-300">
                          <div className="label">Total</div>
                          <div className="value large text-green-800">
                            ${((parseFloat(selectedProduct.precio_compra) || 0) * (parseInt(selectedProduct.stock_actual) || 0)).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Valorización por Venta */}
                    <div className="popup-section">
                      <h4>💜 Valor por Venta</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card bg-purple-50 border-purple-200">
                          <div className="label">Unitario</div>
                          <div className="value medium text-purple-800">
                            ${(parseFloat(selectedProduct.precio_venta) || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="info-card bg-purple-100 border-purple-300">
                          <div className="label">Total</div>
                          <div className="value large text-purple-800">
                            ${((parseFloat(selectedProduct.precio_venta) || 0) * (parseInt(selectedProduct.stock_actual) || 0)).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Margen Potencial */}
                    <div className="popup-section">
                      <h4>🧡 Margen Potencial</h4>
                      <div className="grid grid-cols-1 gap-1.5">
                        <div className="info-card bg-orange-50 border-orange-200">
                          <div className="label">Diferencia Total</div>
                          <div className="value large text-orange-800">
                            ${(((parseFloat(selectedProduct.precio_venta) || 0) - (parseFloat(selectedProduct.precio_compra) || 0)) * (parseInt(selectedProduct.stock_actual) || 0)).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Botones de acción */}
                <div className="popup-actions">
                  <div className="actions-container">
                    <button
                      onClick={() => setShowValorizacionModal(false)}
                      className="btn-secondary"
                      title="Cerrar popup"
                    >
                      <X className="w-4 h-4" />
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        
        {/* Toast de notificaciones */}
        <SimpleToast
          isOpen={toast.isOpen}
          onClose={hideToast}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          showCloseButton={toast.showCloseButton}
          autoClose={toast.autoClose}
          autoCloseDelay={toast.autoCloseDelay}
          onConfirm={toast.onConfirm}
          onCancel={toast.onCancel}
          handleConfirm={handleConfirm}
          handleCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default Productos;